import { UIView, VizType, worldGroupDatum } from "@atlas-int/data";
import { TradeDirection } from "@atlas-int/data/types";
import { computeExporterImporterSwap } from "@atlas-int/controls";
import {
  StandardControlPanel as StandardControlPanelBase,
  MarketsControlPanel as MarketsControlPanelBase,
  ProductsControlPanel as ProductsControlPanelBase,
  SettingsButtonContainer,
} from "@atlas-int/controls";
import UIViewSelectorButton from "../../buttons/UIViewSelectorButton";
import TradeDirectionSelectorButton from "../../buttons/TradeDirectionSelectorButton";
import { ChevronToggle } from "@atlas-int/ui";
import { uiIcons } from "../../../assets/icons";
import { useBaseExploreParams, useExploreParams } from "../../../visualization/paramSchemas";
import LocationDropdown from "../locationDropdown/LocationDropdown";
import ProductDropdown from "../productDropdown/ProductDropdown";
import { DropdownRole } from "../DropdownRole";
import { useHandleExporterImporterSwap } from "../hooks";
import { YearInputs } from "../shared/YearInputs";
import ProductClassToggleGroup from "../ProductClassToggleGroup";
import CompactSettingsDropdown from "../settingsPanel/CompactSettingsDropdown";
import { googleAnalyticsCustomEvent } from "../../../sharedUtilities/analytics";

// ── Inlined sub-panels ──────────────────────────────────────────────

const MarketsControlPanel = () => {
  const [{ tradeDirection: currentTradeDirection, exporter, importer }, setQuery] =
    useExploreParams();

  // In Markets view one location is always World. Show whichever
  // location is *not* World; fall back to trade-direction convention.
  let locationSelectionRole: DropdownRole;
  if (exporter === worldGroupDatum.groupId && importer !== worldGroupDatum.groupId) {
    locationSelectionRole = DropdownRole.LocationImporter;
  } else if (importer === worldGroupDatum.groupId && exporter !== worldGroupDatum.groupId) {
    locationSelectionRole = DropdownRole.LocationExporter;
  } else {
    locationSelectionRole =
      currentTradeDirection === TradeDirection.Imports
        ? DropdownRole.LocationImporter
        : DropdownRole.LocationExporter;
  }

  const handleTradeDirectionSwap = () => {
    googleAnalyticsCustomEvent("btn", "click", "swaptrade", "explore");
    const result = computeExporterImporterSwap({
      importer,
      exporter,
      tradeDirection: currentTradeDirection,
      view: "markets",
      worldGroupId: worldGroupDatum.groupId,
    });
    setQuery(result as { exporter: string; importer: string; tradeDirection: TradeDirection });
  };

  return (
    <MarketsControlPanelBase
      locationDropdown={
        <LocationDropdown locationSelectionRole={locationSelectionRole} />
      }
      productDropdown={<ProductDropdown />}
      exporterButton={
        <TradeDirectionSelectorButton
          targetOption={TradeDirection.Exports}
          selected={currentTradeDirection === TradeDirection.Exports}
        />
      }
      importerButton={
        <TradeDirectionSelectorButton
          targetOption={TradeDirection.Imports}
          selected={currentTradeDirection === TradeDirection.Imports}
        />
      }
      onTradeDirectionSwap={handleTradeDirectionSwap}
      swapIconSrc={uiIcons.swap}
    />
  );
};

const ProductsControlPanel = () => {
  const handleExporterImporterSwap = useHandleExporterImporterSwap();

  return (
    <ProductsControlPanelBase
      exporterDropdown={
        <LocationDropdown
          locationSelectionRole={DropdownRole.LocationExporter}
        />
      }
      importerDropdown={
        <LocationDropdown
          locationSelectionRole={DropdownRole.LocationImporter}
        />
      }
      onSwap={handleExporterImporterSwap}
      swapIconSrc={uiIcons.swap}
    />
  );
};

// ── Main panel ──────────────────────────────────────────────────────

interface StandardControlPanelProps {
  vizType: VizType;
  isSettingsOpen: boolean;
  toggleSettingsPanel: () => void;
}

export const StandardControlPanel = ({
  vizType,
  isSettingsOpen,
  toggleSettingsPanel,
}: StandardControlPanelProps) => {
  const [{ view: currentUIView }] = useBaseExploreParams();

  const mainDropdown =
    currentUIView === UIView.Markets ? (
      <MarketsControlPanel />
    ) : (
      <ProductsControlPanel />
    );

  return (
    <StandardControlPanelBase
      viewSelectorButtons={
        <>
          <UIViewSelectorButton
            uiViewOption={UIView.Products}
            selected={currentUIView === UIView.Products}
            vizType={vizType}
          />
          <UIViewSelectorButton
            uiViewOption={UIView.Markets}
            selected={currentUIView === UIView.Markets}
            vizType={vizType}
          />
        </>
      }
      arrowIconSrc={uiIcons.controlPanelArrow}
      mainDropdown={mainDropdown}
      yearInputs={<YearInputs vizType={vizType} />}
      productClassToggle={<ProductClassToggleGroup />}
      settingsSection={
        <>
          <SettingsButtonContainer
            data-settings-toggle
            onClick={toggleSettingsPanel}
          >
            <img src={uiIcons.settings} alt="Settings" />{" "}
            Settings
            <ChevronToggle isOpen={isSettingsOpen} />
          </SettingsButtonContainer>
          {isSettingsOpen && (
            <CompactSettingsDropdown onClose={toggleSettingsPanel} />
          )}
        </>
      }
    />
  );
};
